import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

// Интерфейсы
interface SyncStatus {
  isRunning: boolean;
  lastSync: string | null;
  nextScheduledSync: string | null;
  recordsCount: number;
}

interface SyncLog {
  id: number;
  status: 'started' | 'success' | 'error';
  message: string;
  records_processed: number;
  started_at: string;
  completed_at: string | null;
  username: string;
}

interface SyncSettings {
  auto_sync_enabled: string;
  sync_schedule: string;
  data_source_path: string;
  backup_before_sync: string;
}

interface FileItem {
  name: string;
  type: 'file' | 'directory';
  size?: number;
  lastModified: string;
  extension?: string;
}

interface FileBrowserData {
  currentPath: string;
  parentPath: string | null;
  items: FileItem[];
}

interface SyncManagementProps {
  onClose?: () => void;
}

const SyncManagement: React.FC<SyncManagementProps> = ({ onClose }) => {
  const { token } = useAuth();
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [syncLogs, setSyncLogs] = useState<SyncLog[]>([]);
  const [syncSettings, setSyncSettings] = useState<SyncSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showFileBrowser, setShowFileBrowser] = useState(false);
  const [fileBrowserData, setFileBrowserData] = useState<FileBrowserData | null>(null);
  const [selectedFilePath, setSelectedFilePath] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'status' | 'settings' | 'logs'>('status');

  // Состояния для удобного выбора времени
  const [scheduleHour, setScheduleHour] = useState<number>(2);
  const [scheduleMinute, setScheduleMinute] = useState<number>(0);
  const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState<number>(0); // 0 = воскресенье
  const [scheduleFrequency, setScheduleFrequency] = useState<'hourly' | 'daily' | 'weekly'>('daily');

  // Загружаем начальные данные
  useEffect(() => {
    loadInitialData();
    const interval = setInterval(loadSyncStatus, 5000); // Обновляем статус каждые 5 секунд
    return () => clearInterval(interval);
  }, []);

  // Преобразовать cron в удобное время
  const parseCronToTime = (cronExpression: string) => {
    if (!cronExpression) return;

    const parts = cronExpression.split(' ');
    if (parts.length === 5) {
      const [minute, hour, day, month, dayOfWeek] = parts;

      // Устанавливаем минуты
      setScheduleMinute(parseInt(minute) || 0);

      // Определяем частоту на основе cron выражения
      if (dayOfWeek !== '*') {
        // Еженедельно
        setScheduleFrequency('weekly');
        setScheduleHour(parseInt(hour) || 2);
        setScheduleDayOfWeek(parseInt(dayOfWeek) || 0);
      } else if (day === '*' && month === '*') {
        if (hour === '*') {
          // Каждый час
          setScheduleFrequency('hourly');
        } else {
          // Ежедневно в определенный час
          setScheduleFrequency('daily');
          setScheduleHour(parseInt(hour) || 2);
        }
      } else {
        // По умолчанию считаем ежедневной синхронизацией
        setScheduleFrequency('daily');
        if (hour !== '*') {
          setScheduleHour(parseInt(hour) || 2);
        }
      }
    }
  };

  // Преобразовать время в cron
  const timeToCron = () => {
    switch (scheduleFrequency) {
      case 'hourly':
        return `${scheduleMinute} * * * *`; // Каждый час в указанную минуту
      case 'daily':
        return `${scheduleMinute} ${scheduleHour} * * *`; // Раз в день в указанное время
      case 'weekly':
        return `${scheduleMinute} ${scheduleHour} * * ${scheduleDayOfWeek}`; // Раз в неделю в выбранный день
      default:
        return syncSettings?.sync_schedule || '0 2 * * *';
    }
  };

  // Получить читаемое описание расписания
  const getScheduleDescription = () => {
    if (!syncSettings?.sync_schedule) return 'Не настроено';

    const parts = syncSettings.sync_schedule.split(' ');
    if (parts.length === 5) {
      const [minute, hour, day, month, dayOfWeek] = parts;

      // Для почасовых задач
      if (hour === '*') {
        const minuteStr = minute.padStart(2, '0');
        return `Каждый час в :${minuteStr}`;
      }

      // Для точного времени
      if (minute !== '*' && hour !== '*') {
        const timeStr = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`;

        if (dayOfWeek !== '*') {
          const days = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
          return `Еженедельно по ${days[parseInt(dayOfWeek)]} в ${timeStr}`;
        } else if (day === '*' && month === '*') {
          return `Ежедневно в ${timeStr}`;
        }
      }
    }

    return `Cron: ${syncSettings.sync_schedule}`;
  };

  // Загружаем настройки и парсим время
  useEffect(() => {
    if (syncSettings?.sync_schedule) {
      parseCronToTime(syncSettings.sync_schedule);
    }
  }, [syncSettings]);

  // Обработчик ESC для закрытия модального окна
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadSyncStatus(),
        loadSyncLogs(),
        loadSyncSettings()
      ]);
    } catch (error) {
      setError('Не удалось загрузить данные синхронизации');
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSyncStatus = async () => {
    try {
      const response = await fetch('/api/sync/status', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load sync status');
      }

      const data = await response.json();
      setSyncStatus(data);
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  const loadSyncLogs = async () => {
    try {
      const response = await fetch('/api/sync/logs?limit=10', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load sync logs');
      }

      const data = await response.json();
      setSyncLogs(data.logs);
    } catch (error) {
      console.error('Error loading sync logs:', error);
    }
  };

  const loadSyncSettings = async () => {
    try {
      const response = await fetch('/api/sync/settings', {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to load sync settings');
      }

      const data = await response.json();
      setSyncSettings(data);
    } catch (error) {
      console.error('Error loading sync settings:', error);
    }
  };

  const handleStartSync = async () => {
    try {
      setSyncing(true);
      setError(null);

      const payload = selectedFilePath ? { filePath: selectedFilePath } : {};
      const response = await fetch('/api/sync/start', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось запустить синхронизацию');
      }

      const data = await response.json();
      setSuccess(`Синхронизация запущена: ${data.message}`);
      setTimeout(() => setSuccess(null), 5000);

      // Обновляем статус и логи
      setTimeout(() => {
        loadSyncStatus();
        loadSyncLogs();
      }, 1000);

    } catch (error: any) {
      setError(error.message || 'Не удалось запустить синхронизацию');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveSettings = async () => {
    if (!syncSettings) return;

    try {
      setLoading(true);

      // Обновляем расписание cron на основе выбранного времени
      const updatedSettings = {
        ...syncSettings,
        sync_schedule: timeToCron()
      };

      const response = await fetch('/api/sync/settings', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedSettings),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось сохранить настройки');
      }

      setSyncSettings(updatedSettings);
      setSuccess('Настройки сохранены успешно');
      setTimeout(() => setSuccess(null), 5000);

      // Перезагружаем статус для обновления информации о следующей синхронизации
      await loadSyncStatus();
    } catch (error: any) {
      setError(error.message || 'Не удалось сохранить настройки');
    } finally {
      setLoading(false);
    }
  };

  const openFileBrowser = async (path: string = '/app/data') => {
    try {
      const response = await fetch(`/api/sync/browse-files?path=${encodeURIComponent(path)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Не удалось открыть браузер файлов');
      }

      const data = await response.json();
      setFileBrowserData(data);
      setShowFileBrowser(true);
    } catch (error: any) {
      setError(error.message || 'Не удалось открыть браузер файлов');
    }
  };

  const navigateToPath = (path: string) => {
    openFileBrowser(path);
  };

  const selectFile = (item: FileItem) => {
    if (item.type === 'file' && item.extension === '.json') {
      const fullPath = `${fileBrowserData?.currentPath}/${item.name}`;
      setSelectedFilePath(fullPath);

      // Если мы в режиме настроек, обновляем поле пути к источнику данных
      if (activeTab === 'settings' && syncSettings) {
        setSyncSettings({
          ...syncSettings,
          data_source_path: fullPath
        });
      }

      setShowFileBrowser(false);
      setSuccess(`Выбран файл: ${item.name}`);
      setTimeout(() => setSuccess(null), 3000);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Неизвестно';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return date.toLocaleString('ru-RU');
  };

  const formatFileSize = (bytes: number) => {
    const sizes = ['Б', 'КБ', 'МБ', 'ГБ'];
    if (bytes === 0) return '0 Б';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800';
      case 'error': return 'bg-red-100 text-red-800';
      case 'started': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          <span className="ml-2 text-gray-600">Загружаем данные синхронизации...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-2 sm:p-6">
      {/* Градиентный заголовок */}
      <div className="relative mb-4 sm:mb-8 bg-gradient-to-r from-indigo-600 via-purple-600 to-blue-600 rounded-2xl p-4 sm:p-8 text-white overflow-hidden shadow-xl">
        {/* Декоративные элементы */}
        <div className="absolute top-0 right-0 -mt-4 -mr-16 w-32 h-32 bg-white opacity-10 rounded-full"></div>
        <div className="absolute bottom-0 left-0 -mb-8 -ml-8 w-20 h-20 bg-white opacity-10 rounded-full"></div>
        <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-white opacity-5 rounded-full"></div>

        <div className="relative z-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-full p-4 mr-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <div>
                <h1 className="text-2xl sm:text-4xl font-bold mb-2">
                  Управление синхронизацией
                </h1>
                <p className="text-white text-opacity-90 text-sm sm:text-lg">
                  🎯 Настройка автоматической синхронизации и мониторинг системы
                </p>
              </div>
            </div>

            {/* Кнопка закрытия */}
            {onClose && (
              <button
                onClick={onClose}
                className="bg-white bg-opacity-20 hover:bg-opacity-30 backdrop-blur-sm rounded-full p-3 transition-all duration-200 hover:scale-110 hover:rotate-90"
                title="Закрыть"
              >
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Краткая статистика */}
          {syncStatus && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-20">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-white text-opacity-80 text-sm font-medium">📁 Записей</div>
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                </div>
                <div className="text-2xl font-bold">{syncStatus.recordsCount.toLocaleString()}</div>
              </div>

              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-20">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-white text-opacity-80 text-sm font-medium">⚡ Статус</div>
                  <div className={`w-2 h-2 rounded-full ${
                    syncStatus.isRunning ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'
                  }`}></div>
                </div>
                <div className="text-lg font-semibold">
                  {syncStatus.isRunning ? (
                    <span className="text-yellow-200">Выполняется</span>
                  ) : (
                    <span className="text-green-200">Готова</span>
                  )}
                </div>
              </div>

              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-20">
                <div className="text-white text-opacity-80 text-sm font-medium mb-2">🕰️ Последняя</div>
                <div className="text-sm font-medium">
                  {syncStatus.lastSync ? formatDate(syncStatus.lastSync) : 'Никогда'}
                </div>
              </div>

              <div className="bg-white bg-opacity-20 backdrop-blur-sm rounded-xl p-5 border border-white border-opacity-20">
                <div className="text-white text-opacity-80 text-sm font-medium mb-2">⏰ Следующая</div>
                <div className="text-sm font-medium">
                  {syncStatus.nextScheduledSync ? formatDate(syncStatus.nextScheduledSync) : 'Не настроено'}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4">
          <div className="flex">
            <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="ml-auto pl-3"
            >
              <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
          <div className="flex">
            <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
            <button
              onClick={() => setSuccess(null)}
              className="ml-auto pl-3"
            >
              <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Модерновые вкладки */}
      <div className="mb-4 sm:mb-8">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-2">
          <nav className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
            {[
              { key: 'status', label: '📊 Статус', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' },
              { key: 'settings', label: '⚙️ Настройки', icon: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4' },
              { key: 'logs', label: '📄 Логи', icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center px-4 sm:px-6 py-3 sm:py-3 rounded-xl font-medium text-sm transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-[1.02]'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={tab.icon} />
                </svg>
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Содержимое вкладок */}
      {activeTab === 'status' && syncStatus && (
        <div className="w-full">
          <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-shadow duration-300 max-w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full p-3 mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                Текущий статус
              </h3>
              <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
                syncStatus.isRunning
                  ? 'bg-yellow-100 text-yellow-800 animate-pulse'
                  : 'bg-green-100 text-green-800'
              }`}>
                {syncStatus.isRunning ? '🔄 Активна' : '✅ Готова'}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Статус:</span>
                <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                  syncStatus.isRunning ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'
                }`}>
                  {syncStatus.isRunning ? 'Выполняется' : 'Готов'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Последняя синхронизация:</span>
                <span className="text-sm text-gray-900">
                  {syncStatus.lastSync ? formatDate(syncStatus.lastSync) : 'Никогда'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-sm text-gray-500">Всего записей:</span>
                <span className="text-sm font-medium text-gray-900">
                  {syncStatus.recordsCount.toLocaleString('ru-RU')}
                </span>
              </div>

              {/* Отображение времени автосинхронизации */}
              <div className="border-t pt-3 mt-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Автосинхронизация:</span>
                  <span className={`text-sm font-medium ${syncSettings?.auto_sync_enabled === 'true' ? 'text-green-600' : 'text-gray-500'}`}>
                    {syncSettings?.auto_sync_enabled === 'true' ? 'Включена' : 'Отключена'}
                  </span>
                </div>
                {syncSettings?.auto_sync_enabled === 'true' && (
                  <div className="mt-2">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Расписание:</span>
                      <span className="text-sm text-gray-900 font-medium">
                        {getScheduleDescription()}
                      </span>
                    </div>
                    {syncStatus.nextScheduledSync && (
                      <div className="flex justify-between mt-1">
                        <span className="text-sm text-gray-500">Следующий запуск:</span>
                        <span className="text-sm text-indigo-600 font-medium">
                          {formatDate(syncStatus.nextScheduledSync)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedFilePath && (
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-sm text-blue-700">
                    <span className="font-medium">Выбран файл:</span> {selectedFilePath.split('/').pop()}
                  </p>
                </div>
              )}
            </div>

            <div className="mt-8 space-y-4">
              <button
                onClick={handleStartSync}
                disabled={syncing || syncStatus.isRunning}
                className={`w-full group relative overflow-hidden rounded-2xl py-4 px-6 text-lg font-semibold text-white transition-all duration-300 transform min-h-[56px] touch-manipulation ${
                  syncing || syncStatus.isRunning
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 hover:scale-[1.02] hover:shadow-2xl'
                }`}
              >
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                <div className="relative flex items-center justify-center">
                  {syncing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      🔄 Запускаем синхронизацию...
                    </>
                  ) : (
                    <>
                      <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      🚀 Запустить синхронизацию
                    </>
                  )}
                </div>
              </button>

              <button
                onClick={() => openFileBrowser()}
                className="w-full group relative overflow-hidden rounded-xl py-3 px-6 text-sm font-semibold transition-all duration-300 transform bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200 text-gray-700 hover:from-indigo-50 hover:to-blue-50 hover:border-indigo-300 hover:text-indigo-700 hover:scale-[1.01] hover:shadow-lg min-h-[48px] touch-manipulation"
              >
                <div className="absolute inset-0 bg-indigo-500 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                <div className="relative flex items-center justify-center">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  📁 Выбрать файл
                </div>
              </button>

              <button
                onClick={loadSyncStatus}
                className="w-full group relative overflow-hidden rounded-xl py-3 px-6 text-sm font-semibold transition-all duration-300 transform bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 text-amber-700 hover:from-amber-100 hover:to-orange-100 hover:border-amber-300 hover:text-amber-800 hover:scale-[1.01] hover:shadow-lg"
              >
                <div className="absolute inset-0 bg-amber-500 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                <div className="relative flex items-center justify-center">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  🔄 Обновить статус
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && syncSettings && (
        <div className="bg-white shadow-xl rounded-2xl p-8 border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
          <div className="space-y-8">
            {/* Автоматическая синхронизация */}
            <div className="pb-8 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div>
                    <label className="text-sm font-medium text-gray-900">
                      Автоматическая синхронизация
                    </label>
                    <p className="text-sm text-gray-500 mt-1">Включить автоматический запуск по расписанию</p>
                  </div>
                </div>
                <button
                onClick={() => setSyncSettings({
                  ...syncSettings,
                  auto_sync_enabled: syncSettings.auto_sync_enabled === 'true' ? 'false' : 'true'
                })}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                  syncSettings.auto_sync_enabled === 'true' ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                  syncSettings.auto_sync_enabled === 'true' ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
              </div>
            </div>

            {/* Расписание синхронизации */}
            <div className="pb-8 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 mb-6">
              <div className="flex items-center mb-4">
                <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <label className="text-sm font-medium text-gray-900">
                  Расписание синхронизации
                </label>
              </div>

              <div className="space-y-4">
                {/* Частота */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Частота синхронизации</label>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setScheduleFrequency('hourly')}
                      className={`px-4 py-4 text-sm font-medium rounded-md border transition-colors touch-manipulation min-h-[48px] ${
                        ['hourly', 'every3hours', 'every6hours', 'every12hours'].includes(scheduleFrequency)
                          ? 'bg-green-50 border-green-500 text-green-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Ежечасно
                    </button>
                    <button
                      type="button"
                      onClick={() => setScheduleFrequency('daily')}
                      className={`px-4 py-4 text-sm font-medium rounded-md border transition-colors touch-manipulation min-h-[48px] ${
                        scheduleFrequency === 'daily'
                          ? 'bg-indigo-50 border-indigo-500 text-indigo-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      Ежедневно
                    </button>
                    <button
                      type="button"
                      onClick={() => setScheduleFrequency('weekly')}
                      className={`px-4 py-4 text-sm font-medium rounded-md border transition-colors touch-manipulation min-h-[48px] ${
                        scheduleFrequency === 'weekly'
                          ? 'bg-purple-50 border-purple-500 text-purple-700'
                          : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <svg className="w-5 h-5 mx-auto mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      Еженедельно
                    </button>
                  </div>
                </div>


                {/* Дни недели */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">
                    📅 Дни недели
                    {scheduleFrequency !== 'weekly' && (
                      <span className="text-xs text-gray-500 ml-2">
                        (все дни автоматически)
                      </span>
                    )}
                  </label>
                  <div className="grid grid-cols-7 gap-1 sm:gap-2">
                    {[
                      { day: 1, short: 'Пн', full: 'Понедельник' },
                      { day: 2, short: 'Вт', full: 'Вторник' },
                      { day: 3, short: 'Ср', full: 'Среда' },
                      { day: 4, short: 'Чт', full: 'Четверг' },
                      { day: 5, short: 'Пт', full: 'Пятница' },
                      { day: 6, short: 'Сб', full: 'Суббота' },
                      { day: 0, short: 'Вс', full: 'Воскресенье' }
                    ].map((dayInfo) => {
                      const isWeeklyMode = scheduleFrequency === 'weekly';
                      const isSelected = isWeeklyMode ? scheduleDayOfWeek === dayInfo.day : true;

                      return (
                        <button
                          key={dayInfo.day}
                          type="button"
                          onClick={() => isWeeklyMode && setScheduleDayOfWeek(dayInfo.day)}
                          className={`px-2 py-3 text-xs rounded-md border transition-colors relative min-h-[44px] touch-manipulation ${
                            isSelected
                              ? isWeeklyMode
                                ? 'bg-indigo-100 border-indigo-500 text-indigo-700 font-semibold'
                                : 'bg-gray-100 border-gray-400 text-gray-600 cursor-not-allowed'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          } ${
                            !isWeeklyMode ? 'cursor-not-allowed' : 'cursor-pointer'
                          }`}
                          title={isWeeklyMode ? dayInfo.full : `${dayInfo.full} (автоматически выбран)`}
                          disabled={!isWeeklyMode}
                        >
                          {dayInfo.short}
                          {isSelected && (
                            <span className={`absolute -top-1 -right-1 text-xs ${
                              isWeeklyMode ? 'text-indigo-600' : 'text-gray-500'
                            }`}>
                              ✓
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {scheduleFrequency === 'weekly' && (
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Выберите день недели для синхронизации
                    </p>
                  )}
                </div>

                {/* Время запуска */}
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">⏰ Время запуска</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Час</label>
                      <select
                        value={scheduleHour}
                        onChange={(e) => setScheduleHour(parseInt(e.target.value))}
                        className={`w-full rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                          scheduleFrequency === 'hourly'
                            ? 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'border-gray-300 bg-white'
                        }`}
                        disabled={scheduleFrequency === 'hourly'}
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {i.toString().padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1">Минута</label>
                      <select
                        value={scheduleMinute}
                        onChange={(e) => setScheduleMinute(parseInt(e.target.value))}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm bg-white"
                      >
                        {(scheduleFrequency === 'hourly'
                          ? [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55]
                          : [0, 15, 30, 45]
                        ).map(minute => (
                          <option key={minute} value={minute}>
                            {minute.toString().padStart(2, '0')}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  {scheduleFrequency === 'hourly' && (
                    <p className="text-xs text-gray-500 mt-2">
                      💡 При ежечасной синхронизации часы отключены - выберите только минуту
                    </p>
                  )}
                </div>


                {/* Предварительный просмотр */}
                <div className="bg-gray-50 p-3 rounded-md">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Расписание:</span> {
                      scheduleFrequency === 'hourly'
                        ? `Каждый час в :${scheduleMinute.toString().padStart(2, '0')}`
                        : scheduleFrequency === 'daily'
                        ? `Ежедневно в ${scheduleHour.toString().padStart(2, '0')}:${scheduleMinute.toString().padStart(2, '0')}`
                        : `Еженедельно по ${
                            ['Воскресеньям', 'Понедельникам', 'Вторникам', 'Средам', 'Четвергам', 'Пятницам', 'Субботам'][scheduleDayOfWeek]
                          } в ${scheduleHour.toString().padStart(2, '0')}:${scheduleMinute.toString().padStart(2, '0')}`
                    }
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Cron: {timeToCron()}
                  </p>
                  {(['hourly', 'every3hours', 'every6hours', 'every12hours'].includes(scheduleFrequency)) && (
                    <p className="text-xs text-green-600 mt-1">
                      ⚡ Частая синхронизация в течение дня
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Источник данных */}
            <div className="pb-8 border-b border-gray-100 bg-gradient-to-r from-green-50 to-teal-50 rounded-2xl p-6 mb-6">
              <div className="flex items-center mb-2">
                <svg className="w-5 h-5 text-gray-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                </svg>
                <label className="text-sm font-medium text-gray-900">
                  Источник данных
                </label>
              </div>
              <div className="mt-1 flex rounded-md shadow-sm">
                <input
                  type="text"
                  value={syncSettings.data_source_path}
                  onChange={(e) => setSyncSettings({
                    ...syncSettings,
                    data_source_path: e.target.value
                  })}
                  className="flex-1 block w-full rounded-none rounded-l-md border-gray-300 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  placeholder="/app/data/data.json"
                />
                <button
                  type="button"
                  onClick={() => {
                    // Открываем браузер и сохраняем выбранный путь в настройки
                    setShowFileBrowser(true);
                    openFileBrowser('/app/data');
                  }}
                  className="inline-flex items-center px-3 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm hover:bg-gray-100"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <span className="ml-2">Обзор</span>
                </button>
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Укажите путь к JSON файлу с данными или выберите через браузер файлов
              </p>
            </div>

            {/* Резервное копирование */}
            <div className="pb-8 border-b border-gray-100 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-2xl p-6 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-start">
                  <svg className="w-5 h-5 text-gray-400 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <label className="text-sm font-medium text-gray-900">
                      Резервное копирование
                    </label>
                    <p className="text-sm text-gray-500 mt-1">Автоматически создавать бэкап перед синхронизацией</p>
                    <p className="text-xs text-yellow-600 mt-1">⚠️ Функция в разработке</p>
                  </div>
                </div>
              <button
                onClick={() => setSyncSettings({
                  ...syncSettings,
                  backup_before_sync: syncSettings.backup_before_sync === 'true' ? 'false' : 'true'
                })}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                  syncSettings.backup_before_sync === 'true' ? 'bg-indigo-600' : 'bg-gray-200'
                }`}
              >
                <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition duration-200 ease-in-out ${
                  syncSettings.backup_before_sync === 'true' ? 'translate-x-5' : 'translate-x-0'
                }`} />
              </button>
              </div>
            </div>

            {/* Кнопка сохранения */}
            <div className="pt-4">
              <button
                onClick={handleSaveSettings}
                disabled={loading}
                className="w-full group relative overflow-hidden rounded-2xl py-4 px-8 text-lg font-bold text-white transition-all duration-300 transform bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:scale-[1.02] hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                <div className="relative flex items-center justify-center">
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-6 w-6 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="m4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      ⏳ Сохраняем...
                    </>
                  ) : (
                    <>
                      <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      💾 Сохранить настройки
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div className="bg-white shadow-xl rounded-xl sm:rounded-2xl p-4 sm:p-8 border border-gray-100 hover:shadow-2xl transition-shadow duration-300">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8 gap-4">
            <div className="flex items-center">
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-full p-2 sm:p-3 mr-3 sm:mr-4">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-900">📄 История синхронизации</h3>
            </div>
            <button
              onClick={loadSyncLogs}
              className="group relative overflow-hidden rounded-lg sm:rounded-xl py-2 sm:py-3 px-4 sm:px-6 text-sm font-semibold transition-all duration-300 transform bg-gradient-to-r from-emerald-50 to-green-50 border-2 border-emerald-200 text-emerald-700 hover:from-emerald-100 hover:to-green-100 hover:border-emerald-300 hover:text-emerald-800 hover:scale-[1.01] hover:shadow-lg w-full sm:w-auto"
            >
              <div className="absolute inset-0 bg-emerald-500 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              <div className="relative flex items-center justify-center">
                <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                🔄 Обновить
              </div>
            </button>
          </div>

          {/* Desktop Table */}
          <div className="hidden md:block overflow-x-auto bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-2">
            <table className="min-w-full divide-y divide-gray-200 bg-white rounded-xl shadow-sm">
              <thead className="bg-gradient-to-r from-indigo-50 to-purple-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider rounded-tl-xl">
                    🕰️ Дата/время
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                    🔆 Статус
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                    💬 Сообщение
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider">
                    📈 Записей
                  </th>
                  <th className="px-6 py-4 text-left text-sm font-bold text-gray-700 uppercase tracking-wider rounded-tr-xl">
                    👤 Пользователь
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {syncLogs.length > 0 ? (
                  syncLogs.map((log, index) => (
                    <tr key={log.id} className="hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 transition-all duration-200">
                      <td className="px-6 py-5 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatDate(log.started_at)}
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap">
                        <span className={`inline-flex px-3 py-2 rounded-full text-xs font-bold shadow-sm ${getStatusColor(log.status)}`}>
                          {log.status === 'success' && '✅ Успешно'}
                          {log.status === 'error' && '❌ Ошибка'}
                          {log.status === 'started' && '⏳ Запущено'}
                          {!['success', 'error', 'started'].includes(log.status) && log.status}
                        </span>
                      </td>
                      <td className="px-6 py-5 text-sm text-gray-900 max-w-xs">
                        <div className="truncate" title={log.message}>
                          {log.message}
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap text-sm font-semibold text-gray-900">
                        <div className="flex items-center">
                          <span className="bg-gradient-to-r from-blue-100 to-indigo-100 px-2 py-1 rounded-md text-blue-800 font-bold">
                            {(log.records_processed || 0).toLocaleString('ru-RU')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-900">
                        {log.username ? (
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mr-2">
                              <span className="text-white text-xs font-bold">
                                {log.username.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium">{log.username}</span>
                          </div>
                        ) : (
                          <span className="inline-flex px-3 py-2 rounded-full text-xs font-bold bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 shadow-sm">
                            🤖 Авто
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-full p-6 mb-4">
                          <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <p className="text-lg font-medium text-gray-500 mb-2">📄 Нет записей в истории</p>
                        <p className="text-sm text-gray-400">Запустите синхронизацию для создания истории</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-4">
            {syncLogs.length > 0 ? (
              syncLogs.map((log, index) => (
                <div key={log.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:shadow-lg transition-all duration-200">
                  {/* Log Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center">
                      <span className={`inline-flex px-2 py-1 rounded-full text-xs font-bold shadow-sm ${getStatusColor(log.status)}`}>
                        {log.status === 'success' && '✅'}
                        {log.status === 'error' && '❌'}
                        {log.status === 'started' && '⏳'}
                        {!['success', 'error', 'started'].includes(log.status) && '❓'}
                      </span>
                      <span className="ml-2 text-sm font-medium text-gray-900">
                        {log.status === 'success' && 'Успешно'}
                        {log.status === 'error' && 'Ошибка'}
                        {log.status === 'started' && 'Запущено'}
                        {!['success', 'error', 'started'].includes(log.status) && log.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 text-right">
                      {formatDate(log.started_at)}
                    </div>
                  </div>

                  {/* Log Details */}
                  <div className="space-y-2">
                    {/* Message */}
                    <div className="flex items-start">
                      <span className="text-xs font-medium text-gray-500 w-16 flex-shrink-0">💬 Сообщение:</span>
                      <div className="text-sm text-gray-900 break-words flex-1">
                        {log.message}
                      </div>
                    </div>

                    {/* Records */}
                    <div className="flex items-center">
                      <span className="text-xs font-medium text-gray-500 w-16 flex-shrink-0">📈 Записей:</span>
                      <span className="bg-gradient-to-r from-blue-100 to-indigo-100 px-2 py-1 rounded-md text-blue-800 font-bold text-sm">
                        {(log.records_processed || 0).toLocaleString('ru-RU')}
                      </span>
                    </div>

                    {/* User */}
                    <div className="flex items-center">
                      <span className="text-xs font-medium text-gray-500 w-16 flex-shrink-0">👤 Кто:</span>
                      {log.username ? (
                        <div className="flex items-center">
                          <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mr-2">
                            <span className="text-white text-xs font-bold">
                              {log.username.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <span className="font-medium text-sm">{log.username}</span>
                        </div>
                      ) : (
                        <span className="inline-flex px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-purple-100 to-pink-100 text-purple-800 shadow-sm">
                          🤖 Авто
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="flex flex-col items-center">
                  <div className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-full p-6 mb-4">
                    <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <p className="text-lg font-medium text-gray-500 mb-2">📄 Нет записей в истории</p>
                  <p className="text-sm text-gray-400">Запустите синхронизацию для создания истории</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Модальное окно браузера файлов */}
      {showFileBrowser && fileBrowserData && (
        <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
          <div className="relative mx-auto p-8 border w-full max-w-5xl shadow-2xl rounded-3xl bg-white">
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center">
                <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full p-3 mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">📁 Выберите файл данных</h3>
              </div>
              <button
                onClick={() => setShowFileBrowser(false)}
                className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Хлебные крошки */}
            <div className="mb-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4">
              <div className="text-sm text-gray-600 font-medium mb-2">🗺️ Текущий путь:</div>
              <div className="flex items-center flex-wrap gap-2">
                <span className="inline-flex items-center px-3 py-1 rounded-lg bg-indigo-100 text-indigo-700 text-sm font-semibold">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  root
                </span>
                {fileBrowserData.currentPath.split('/').filter(Boolean).map((part, index, array) => {
                  const path = '/' + array.slice(0, index + 1).join('/');
                  return (
                    <div key={path} className="flex items-center">
                      <svg className="w-4 h-4 text-gray-400 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                      </svg>
                      <button
                        onClick={() => navigateToPath(path)}
                        className="inline-flex items-center px-3 py-1 rounded-lg bg-blue-100 text-blue-700 text-sm font-semibold hover:bg-blue-200 transition-colors duration-200"
                      >
                        {part}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Кнопка "Назад" */}
            {fileBrowserData.parentPath && (
              <button
                onClick={() => navigateToPath(fileBrowserData.parentPath!)}
                className="mb-6 group relative overflow-hidden rounded-xl py-3 px-6 text-sm font-semibold transition-all duration-300 transform bg-gradient-to-r from-gray-50 to-gray-100 border-2 border-gray-200 text-gray-700 hover:from-gray-100 hover:to-gray-200 hover:border-gray-300 hover:text-gray-900 hover:scale-[1.01] hover:shadow-md"
              >
                <div className="absolute inset-0 bg-gray-500 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
                <div className="relative flex items-center">
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  ⬅️ Назад
                </div>
              </button>
            )}

            {/* Список файлов */}
            <div className="max-h-[500px] overflow-y-auto bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl p-4">
              <div className="bg-white rounded-xl shadow-sm divide-y divide-gray-100">
                {fileBrowserData.items.map((item, index) => (
                  <div
                    key={index}
                    onClick={() => {
                      if (item.type === 'directory') {
                        navigateToPath(`${fileBrowserData.currentPath}/${item.name}`);
                      } else {
                        selectFile(item);
                      }
                    }}
                    className="flex items-center p-4 hover:bg-gradient-to-r hover:from-indigo-50 hover:to-purple-50 cursor-pointer transition-all duration-200 first:rounded-t-xl last:rounded-b-xl"
                  >
                    <div className="flex-shrink-0 mr-4">
                      {item.type === 'directory' ? (
                        <div className="bg-gradient-to-r from-blue-400 to-indigo-500 rounded-lg p-3">
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                          </svg>
                        </div>
                      ) : (
                        <div className="bg-gradient-to-r from-gray-400 to-gray-500 rounded-lg p-3">
                          <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center mb-1">
                        <p className="text-lg font-semibold text-gray-900 truncate mr-2">
                          {item.name}
                        </p>
                        {item.type === 'directory' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800">
                            📁 Папка
                          </span>
                        )}
                        {item.type === 'file' && item.extension === '.json' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold bg-green-100 text-green-800">
                            🖾JSON
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {item.type === 'file' && item.size ? (
                          <div className="flex items-center space-x-4">
                            <span className="inline-flex items-center px-2 py-1 rounded-md bg-gray-100 text-gray-700 text-xs font-medium">
                              📄 {formatFileSize(item.size)}
                            </span>
                            <span className="text-xs text-gray-500">
                              📅 {formatDate(item.lastModified)}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">
                            📅 {formatDate(item.lastModified)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyncManagement;